import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { storesAPI, Store, Listing, s3API, followsAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import ListingCard from '../components/ListingCard';
import DisplayStarRating from '../components/DisplayStarRating';
import { createSlug } from '../utils/slug';
import MapboxMapWrapper from '../components/MapboxMapWrapper';

const StorePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [itemQuantities, setItemQuantities] = useState<Map<number, number>>(new Map());
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [showStoreDetailsModal, setShowStoreDetailsModal] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [checkingFollowStatus, setCheckingFollowStatus] = useState(true);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [storeCoords, setStoreCoords] = useState<[number, number] | null>(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [storeFeedback, setStoreFeedback] = useState<{ last_12_months: { positive: number; neutral: number; negative: number } } | null>(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [storeItemsDisplayedCount, setStoreItemsDisplayedCount] = useState(10);
  const storeItemsInitialCount = 10;
  const storeItemsPageSize = 12;
  const profileInputRef = useRef<HTMLInputElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);

  const loadStore = useCallback(async () => {
    if (!slug) return;
    try {
      const data = await storesAPI.getBySlug(slug);
      setStore(data);
      if (data && createSlug(data.name) !== slug && !slug.match(/^\d+$/)) {
        navigate(`/stores/${createSlug(data.name)}`, { replace: true });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load store');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    if (slug) {
      loadStore();
    }
  }, [slug, loadStore]);

  useEffect(() => {
    if (store?.id && (store.ratings?.total ?? 0) > 0) {
      storesAPI.getFeedback(store.id)
        .then((data: any) => setStoreFeedback(data))
        .catch(() => setStoreFeedback(null));
    } else {
      setStoreFeedback(null);
    }
  }, [store?.id, store?.ratings?.total]);

  useEffect(() => {
    const isOwnerCheck = user && store && user.id === store.owner_id;
    if (store && user && !isOwnerCheck) {
      checkFollowStatus();
      loadFollowerCount();
    } else {
      setCheckingFollowStatus(false);
    }
    // Intentionally only when store/user change; checkFollowStatus/loadFollowerCount are stable in practice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, user]);

  const checkFollowStatus = async () => {
    if (!user || !store || isOwner) return;
    try {
      setCheckingFollowStatus(true);
      const data = await followsAPI.checkStoreFollowStatus(store.id);
      setIsFollowing(data.isFollowing);
    } catch (err) {
      console.error('Error checking follow status:', err);
    } finally {
      setCheckingFollowStatus(false);
    }
  };

  const loadFollowerCount = async () => {
    if (!store) return;
    try {
      const data = await followsAPI.getStoreFollowerCount(store.id);
      setFollowerCount(data.count);
    } catch (err) {
      console.error('Error loading follower count:', err);
    }
  };

  const handleFollow = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!store) return;

    try {
      if (isFollowing) {
        await followsAPI.unfollowStore(store.id);
        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
      } else {
        await followsAPI.followStore(store.id);
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
      }
    } catch (err: any) {
      console.error('Error following/unfollowing store:', err);
      toast.error(err.response?.data?.error || 'Failed to update follow status');
    }
  };

  useEffect(() => {
    // Get user's current location for distance calculation
    let isMounted = true;
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (isMounted) {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          }
        },
        () => {
          // User denied location or error occurred
        }
      );
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    // Get store coordinates from address
    let isMounted = true;
    let abortController: AbortController | null = null;

    if (store) {
      setMapLoading(true);
      
      // Check if store has direct latitude/longitude
      if ((store as any).latitude && (store as any).longitude) {
        if (isMounted) {
          setStoreCoords([parseFloat((store as any).latitude), parseFloat((store as any).longitude)]);
          setMapLoading(false);
        }
        return () => {
          isMounted = false;
        };
      }

      // Otherwise, geocode from address
      if (store.address) {
        const fetchStoreCoords = async () => {
          abortController = new AbortController();
          
          try {
            // Build address string - prioritize the full address entered
            // The store.address should contain the full address from autocomplete
            let address = store.address;
            
            // If address doesn't already include city/state, append them
            if (store.address && !store.address.includes(store.city_name || '') && !store.address.includes(store.state_abbreviation || '')) {
              const addressParts = [store.address];
              if (store.city_name) addressParts.push(store.city_name);
              if (store.state_abbreviation || store.state_name) {
                addressParts.push(store.state_abbreviation || store.state_name || '');
              }
              address = addressParts.filter(Boolean).join(', ');
            }
            
            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&addressdetails=1`,
              {
                headers: {
                  'User-Agent': 'BuyDrop/1.0'
                },
                signal: abortController.signal
              }
            );
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (!isMounted) return;
            
            if (data && data.length > 0) {
              const lat = parseFloat(data[0].lat);
              const lon = parseFloat(data[0].lon);
              setStoreCoords([lat, lon]);
              setMapLoading(false);
            } else {
              // Try with just city and state
              if (store.city_name && (store.state_name || store.state_abbreviation)) {
                const fallbackAddress = `${store.city_name}, ${store.state_abbreviation || store.state_name}`;
                const fallbackController = new AbortController();
                abortController = fallbackController;
                
                const fallbackResponse = await fetch(
                  `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fallbackAddress)}&limit=1&addressdetails=1`,
                  {
                    headers: {
                      'User-Agent': 'BuyDrop/1.0'
                    },
                    signal: fallbackController.signal
                  }
                );
                const fallbackData = await fallbackResponse.json();
                
                if (!isMounted) return;
                
                if (fallbackData && fallbackData.length > 0) {
                  const lat = parseFloat(fallbackData[0].lat);
                  const lon = parseFloat(fallbackData[0].lon);
                  setStoreCoords([lat, lon]);
                  setMapLoading(false);
                } else {
                  setStoreCoords([38.5816, -121.4944]);
                  setMapLoading(false);
                }
              } else {
                setStoreCoords([38.5816, -121.4944]);
                setMapLoading(false);
              }
            }
          } catch (error: any) {
            if (error.name === 'AbortError') {
              return; // Request was aborted, component unmounted
            }
            if (isMounted) {
              // Fallback to a default location (Sacramento, CA)
              setStoreCoords([38.5816, -121.4944]);
              setMapLoading(false);
            }
          }
        };
        fetchStoreCoords();
      } else {
        // If store exists but no address, use default location
        if (isMounted) {
          setStoreCoords([38.5816, -121.4944]);
          setMapLoading(false);
        }
      }
    }
    
    return () => {
      isMounted = false;
      if (abortController) {
        abortController.abort();
      }
    };
  }, [store]);

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const parseStoreHours = () => {
    if (!store?.hours) return null;
    
    // Parse hours if it's a string
    let hoursObj: { [key: string]: { open: string; close: string; closed: boolean } } | null = null;
    if (typeof store.hours === 'string') {
      try {
        hoursObj = JSON.parse(store.hours);
      } catch (e) {
        return null;
      }
    } else if (typeof store.hours === 'object') {
      hoursObj = store.hours;
    }
    
    return hoursObj;
  };

  const getTodayOpenTime = () => {
    const hoursObj = parseStoreHours();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const todayName = days[new Date().getDay()];
    if (hoursObj) {
      const todayHours = hoursObj[todayName];
      if (todayHours && !todayHours.closed) {
        if (todayHours.open) return formatTime(todayHours.open);
        if (todayHours.close) return '9:00 AM';
      }
      for (const d of days) {
        const h = hoursObj[d];
        if (h && !h.closed && h.open) return formatTime(h.open);
      }
    }
    return store?.open_time ? formatTime(store.open_time) : null;
  };

  const getTodayCloseTime = () => {
    const hoursObj = parseStoreHours();
    if (!hoursObj) return store?.close_time ? formatTime(store.close_time) : null;
    
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = new Date().getDay();
    const todayName = days[today];
    const todayHours = hoursObj[todayName];
    
    if (todayHours && !todayHours.closed) {
      return formatTime(todayHours.close);
    }
    
    // Find next open day
    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (today + i) % 7;
      const nextDayName = days[nextDayIndex];
      const nextDayHours = hoursObj[nextDayName];
      if (nextDayHours && !nextDayHours.closed) {
        return formatTime(nextDayHours.close);
      }
    }
    
    return store?.close_time ? formatTime(store.close_time) : null;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };


  const handleDelete = async () => {
    if (!store) return;
    if (!window.confirm('Are you sure you want to delete this store? This will not delete listings associated with it.')) {
      return;
    }

    try {
      await storesAPI.delete(store.id);
      navigate('/sell');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to delete store');
    }
  };

  const isOwner = user && store && user.id === store.owner_id;

  // Helper to render store hours modal
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const getStoreDetailsModal = () => {
    if (!showStoreDetailsModal || !store) return null;
    const hoursObj = parseStoreHours();
    const distance = userLocation && storeCoords
      ? calculateDistance(userLocation.lat, userLocation.lng, storeCoords[0], storeCoords[1])
      : null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4 overflow-y-auto" onClick={() => setShowStoreDetailsModal(false)} style={{ zIndex: 99999 }}>
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()} style={{ zIndex: 100000 }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Store Details</h3>
            <button
              onClick={() => setShowStoreDetailsModal(false)}
              className="text-gray-400 hover:text-gray-600 transition z-[10001] relative bg-white rounded-full p-2 shadow-lg"
              style={{ zIndex: 10001 }}
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="space-y-6">
            {/* License Information */}
            {(store as any).license_number && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">License Information</h4>
                <p className="text-gray-700">{(store as any).license_number}</p>
              </div>
            )}

            {/* Info Section */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Info</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <i className="ri-truck-line text-gray-600"></i>
                  <span className="text-gray-700">Delivery</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-gray-600"></i>
                  <span className="text-gray-700">
                    {(getTodayOpenTime() && getTodayCloseTime())
                      ? `Open today ${getTodayOpenTime()} - ${getTodayCloseTime()}`
                      : (store.open_time && store.close_time)
                        ? `Open today ${formatTime(store.open_time)} - ${formatTime(store.close_time)}`
                        : 'Open'}
                  </span>
                </div>
                {(store as any).medical_recreational && (
                  <div className="flex items-center gap-2">
                    <i className="ri-medicine-bottle-line text-gray-600"></i>
                    <span className="text-gray-700">Medical & recreational</span>
                  </div>
                )}
              </div>
            </div>

            {/* Location */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Location</h4>
              <p className="text-gray-700">
                {store.address}
                {store.city_name && store.state_name && !store.address.includes(store.city_name) && `, ${store.city_name}, ${store.state_name}`}
                {(store as any).zip_code && ` ${(store as any).zip_code}`}
              </p>
              {distance && (
                <p className="text-sm text-gray-600 mt-1">
                  <i className="ri-map-pin-line"></i> {distance.toFixed(1)} miles from you
                </p>
              )}
            </div>

            {/* Contact Information */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Contact</h4>
              <div className="space-y-2">
                {(store as any).phone && (
                  <div className="flex items-center gap-2">
                    <i className="ri-phone-line text-gray-600"></i>
                    <a href={`tel:${(store as any).phone}`} className="text-green-600 hover:text-green-700 hover:underline">
                      {(store as any).phone}
                    </a>
                  </div>
                )}
                {(store as any).email && (
                  <div className="flex items-center gap-2">
                    <i className="ri-mail-line text-gray-600"></i>
                    <a href={`mailto:${(store as any).email}`} className="text-green-600 hover:text-green-700 hover:underline">
                      {(store as any).email}
                    </a>
                  </div>
                )}
                {(() => {
                  const url = String((store as any).website || '').trim();
                  return url ? (
                  <div className="flex items-center gap-2">
                    <i className="ri-global-line text-gray-600"></i>
                    <a
                      href={url.startsWith('http') ? url : `https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-700 hover:underline"
                    >
                      Website
                    </a>
                  </div>
                  ) : null;
                })()}
                {(store as any).instagram && (
                  <div className="flex items-center gap-2">
                    <i className="ri-instagram-line text-gray-600"></i>
                    <a href={(store as any).instagram} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 hover:underline">
                      Instagram
                    </a>
                  </div>
                )}
                {(store as any).facebook && (
                  <div className="flex items-center gap-2">
                    <i className="ri-facebook-line text-gray-600"></i>
                    <a href={(store as any).facebook} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 hover:underline">
                      Facebook
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* About Us */}
            {(store as any).about && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">About Us</h4>
                <p className="text-gray-700 whitespace-pre-wrap">{(store as any).about}</p>
              </div>
            )}

            {/* Hours of Operation */}
            {(hoursObj || (store.open_time && store.close_time)) && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Hours of Operation</h4>
                {hoursObj ? (
                  <div className="space-y-3">
                    {Object.entries(hoursObj).map(([day, { open, close, closed }]) => (
                      <div key={day} className="py-2 border-b border-gray-100 last:border-0">
                        <div className="text-gray-700 font-semibold mb-1">{day}:</div>
                        {closed ? (
                          <div className="text-gray-500">Closed</div>
                        ) : (
                          <div className="text-gray-900">
                            {formatTime(open)} - {formatTime(close)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : store.open_time && store.close_time ? (
                  <div className="space-y-3">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <div key={day} className="py-2 border-b border-gray-100 last:border-0">
                        <div className="text-gray-700 font-semibold mb-1">{day}:</div>
                        <div className="text-gray-900">
                          {formatTime(store.open_time!)} - {formatTime(store.close_time!)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {/* Map */}
            <div className="w-full" style={{ height: 'calc(100vh - 300px)', minHeight: '500px', position: 'relative' }}>
              <h4 className="font-semibold text-gray-900 mb-2">Delivery Location</h4>
              {mapLoading || !storeCoords ? (
                <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200" style={{ minHeight: '500px' }}>
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
                    <p className="text-gray-500 mb-2">Loading map...</p>
                    <p className="text-xs text-gray-400">Fetching location coordinates</p>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full rounded-lg overflow-hidden border border-gray-200" style={{ position: 'relative', zIndex: 1, minHeight: '500px' }}>
                  <MapboxMapWrapper
                    center={storeCoords}
                    zoom={15}
                    markers={[
                      {
                        position: storeCoords,
                        content: (
                          <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg shadow-lg">
                            🏪
                          </div>
                        ),
                        popup: <div>{store.name}</div>,
                      },
                    ]}
                    height="100%"
                    scrollZoom={true}
                    className="rounded-lg"
                    style={{ position: 'relative', zIndex: 1, minHeight: '500px' }}
                    onLoad={() => {
                      console.log('✅ Map loaded successfully');
                      setMapLoading(false);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getStoreHoursModal = () => {
    if (!showHoursModal || !store || !store.hours) return null;
    const hoursObj = parseStoreHours();
    if (!hoursObj) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowHoursModal(false)}>
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-bold text-gray-900">Store Hours</h3>
            <button
              onClick={() => setShowHoursModal(false)}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <div className="space-y-2">
            {Object.entries(hoursObj).map(([day, { open, close, closed }]) => (
              <div key={day} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <span className="text-gray-700 font-medium">{day}:</span>
                {closed ? (
                  <span className="text-gray-500">Closed</span>
                ) : (
                  <span className="text-gray-900">
                    {formatTime(open)} - {formatTime(close)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !store) return;

    setUploadingProfile(true);
    try {
      const { uploadUrl, fileUrl } = await s3API.uploadStoreImage(file.name, file.type, 'profile', store.id);
      
      // Upload to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // Update store profile
      await storesAPI.updateProfilePicture(store.id, fileUrl);
      
      // Reload store
      await loadStore();
      toast.success('Profile picture updated successfully!');
    } catch (err: any) {
      console.error('Error uploading profile picture:', err);
      toast.error(err.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setUploadingProfile(false);
      if (profileInputRef.current) {
        profileInputRef.current.value = '';
      }
    }
  };

  const handleBackgroundImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !store) return;

    setUploadingBackground(true);
    try {
      const { uploadUrl, fileUrl } = await s3API.uploadStoreImage(file.name, file.type, 'background', store.id);
      
      // Upload to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // Update store profile
      await storesAPI.updateBackgroundImage(store.id, fileUrl);
      
      // Reload store
      await loadStore();
      toast.success('Background image updated successfully!');
    } catch (err: any) {
      console.error('Error uploading background image:', err);
      toast.error(err.response?.data?.error || 'Failed to upload background image');
    } finally {
      setUploadingBackground(false);
      if (backgroundInputRef.current) {
        backgroundInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading store...</p>
        </div>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600">{error || 'Store not found'}</p>
          <Link
            to="/"
            className="mt-4 inline-block px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Banner - Full Width on Mobile, Contained on Desktop */}
      <div className="relative w-full md:max-w-6xl md:mx-auto md:px-4 h-56 sm:h-64 md:h-72 overflow-hidden md:mt-6 mb-4 md:mb-6 md:rounded-2xl">
        {store.background_image ? (
          <img 
            src={store.background_image} 
            alt="Background" 
            className="w-full h-full object-cover md:rounded-2xl"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-blue-500 to-purple-600 md:rounded-2xl"></div>
        )}
        
        {/* Change Background Button */}
        {isOwner && (
          <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20">
            <input
              ref={backgroundInputRef}
              type="file"
              accept="image/*"
              onChange={handleBackgroundImageUpload}
              className="hidden"
              id="store-background-upload"
            />
            <label
              htmlFor="store-background-upload"
              className={`px-3 py-1.5 sm:px-4 sm:py-2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 font-medium rounded-lg cursor-pointer transition text-xs sm:text-sm ${
                uploadingBackground ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {uploadingBackground ? 'Uploading...' : '📷 Change'}
            </label>
          </div>
        )}
      </div>

      {/* Content Container */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
        {/* Profile Picture - Half Overlapping Banner */}
        <div className="relative -mt-12 sm:-mt-16 md:-mt-20 mb-4">
          <div className="relative inline-block">
            {store.profile_picture ? (
              <img
                src={store.profile_picture}
                alt={store.name}
                className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full border-4 border-white object-cover shadow-md"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-green-500 rounded-full border-4 border-white flex items-center justify-center text-white text-2xl sm:text-3xl md:text-4xl font-bold shadow-md">
                <i className="ri-store-line"></i>
              </div>
            )}
            {isOwner && (
              <div className="absolute bottom-0 right-0">
                <input
                  ref={profileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  className="hidden"
                  id="store-profile-upload"
                />
                <label
                  htmlFor="store-profile-upload"
                  className={`w-7 h-7 sm:w-8 sm:h-8 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center cursor-pointer transition shadow-lg ${
                    uploadingProfile ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Change profile picture"
                >
                  {uploadingProfile ? (
                    <i className="ri-loader-4-line animate-spin text-xs"></i>
                  ) : (
                    <i className="ri-camera-line text-xs"></i>
                  )}
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Store Name and Info - Below Profile Picture */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">
              {store.name}
            </h2>
            <div className="ml-auto flex gap-2">
              {isOwner && (
                <>
                  <button
                    onClick={() => navigate('/sell')}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-3 py-1.5 rounded-lg transition text-xs sm:text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleDelete}
                    className="bg-red-500 hover:bg-red-600 text-white font-medium px-3 py-1.5 rounded-lg transition text-xs sm:text-sm"
                  >
                    Delete
                  </button>
                </>
              )}
              {user && !isOwner && (
                <>
                  <button
                    onClick={handleFollow}
                    disabled={checkingFollowStatus}
                    className={`px-3 sm:px-4 py-1.5 font-medium rounded-lg transition flex items-center gap-2 text-xs sm:text-sm ${
                      isFollowing
                        ? 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    } ${checkingFollowStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {checkingFollowStatus ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span className="hidden sm:inline">Loading...</span>
                      </>
                    ) : isFollowing ? (
                      <>
                        <i className="ri-user-follow-fill"></i>
                        <span className="hidden sm:inline">Following</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-user-add-line"></i>
                        <span className="hidden sm:inline">Follow</span>
                      </>
                    )}
                  </button>
                  {store.owner_id && (
                    <button
                      onClick={() => navigate('/messages', { state: { sellerId: store.owner_id } })}
                      className="px-3 sm:px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition text-xs sm:text-sm flex items-center gap-2"
                    >
                      <i className="ri-message-3-line"></i>
                      <span className="hidden sm:inline">Message Seller</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
              
          {/* Location */}
          {store.city_name && store.state_name && (
            <div className="text-xs sm:text-sm text-gray-500 mb-2">
              📍 {store.city_name}, {store.state_name}
            </div>
          )}
          
          {/* Service Tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs sm:text-sm font-medium">
              Delivery
            </span>
            <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs sm:text-sm font-medium">
              With tracking
            </span>
            {store.returns_money_back && (
              <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs sm:text-sm font-medium">
                Returns, money back
              </span>
            )}
          </div>
          
          {/* Ratings and Info */}
          <div className="space-y-1.5">
            {store.ratings && store.ratings.total > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5 text-sm">
                <span className="font-medium text-green-600">
                  {Number(store.ratings.positive_percentage ?? 100).toFixed(
                    Number(store.ratings.positive_percentage ?? 100) % 1 === 0 ? 0 : 1
                  )}% Buyer Approved
                </span>
                <span className="text-gray-500">
                  ({store.ratings.total} {store.ratings.total === 1 ? 'rating' : 'ratings'})
                </span>
                <span className="text-gray-300">|</span>
                <span 
                  className="text-gray-600 hover:text-gray-900 underline cursor-pointer"
                  onClick={() => setShowStoreDetailsModal(true)}
                >
                  Store details
                </span>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5 text-sm">
                <span className="text-gray-500">No ratings yet</span>
                <span className="text-gray-300">|</span>
                <span 
                  className="text-gray-600 hover:text-gray-900 underline cursor-pointer"
                  onClick={() => setShowStoreDetailsModal(true)}
                >
                  Store details
                </span>
              </div>
            )}
            {followerCount > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 text-sm text-gray-500">
                <i className="ri-user-follow-line"></i>
                <span>{followerCount} {followerCount === 1 ? 'follower' : 'followers'}</span>
              </div>
            )}
            
            {/* Delivery Info */}
            <div className="flex flex-wrap items-center gap-1.5 text-sm text-gray-700">
              <i className="ri-car-line text-gray-600"></i>
              <span>$2.99 delivery</span>
              <span className="text-gray-300">|</span>
              <span>1 - 2 hr eta</span>
            </div>
            
            {/* Operating Hours */}
            {(store.hours || (store.open_time && store.close_time)) && (getTodayCloseTime() || store.close_time) && (
              <div className="flex items-center gap-1.5 text-sm">
                <i className="ri-time-line text-green-600"></i>
                <button
                  onClick={() => setShowHoursModal(true)}
                  className="text-gray-900 hover:text-gray-700 transition cursor-pointer flex items-center gap-1"
                >
                  <span>
                    {(getTodayOpenTime() && getTodayCloseTime())
                      ? `${getTodayOpenTime()} - ${getTodayCloseTime()}`
                      : `Store open until ${getTodayCloseTime() || formatTime(store.close_time!)}`}
                  </span>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        {store.ratings && store.ratings.total > 0 && store.ratings.reviews && store.ratings.reviews.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Reviews & Ratings</h2>

            <div className="flex flex-col md:flex-row md:gap-8 md:pb-6 md:border-b md:border-gray-200">
              <div className="flex-1 space-y-3 pb-6 md:pb-0 border-b md:border-b-0 border-gray-200 md:border-0">
                {(() => {
                  const pos = storeFeedback?.last_12_months?.positive ?? 0;
                  const neu = storeFeedback?.last_12_months?.neutral ?? 0;
                  const neg = storeFeedback?.last_12_months?.negative ?? 0;
                  // Always use server all-time % so store page / listing badge match.
                  const positivePct = Number(store.ratings.positive_percentage ?? 0).toFixed(
                    Number(store.ratings.positive_percentage ?? 0) % 1 === 0 ? 0 : 1
                  );
                  return (
                    <>
                      <div className="flex items-center gap-3">
                        <span className="text-4xl font-bold text-gray-900">
                          {store.ratings.average.toFixed(2)}
                        </span>
                        <DisplayStarRating rating={store.ratings.average} />
                      </div>
                      <p className="text-sm text-gray-700 flex items-center gap-1.5">
                        <i className="ri-checkbox-circle-fill text-green-500 text-base" />
                        <span>
                          {positivePct}% Buyer Approved • {store.ratings.total}{' '}
                          {store.ratings.total === 1 ? 'review' : 'reviews'}
                        </span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-900">
                          Approved {pos.toLocaleString()}
                        </span>
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                          Neutral {neu.toLocaleString()}
                        </span>
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                          Not approved {neg.toLocaleString()}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="hidden md:block w-px bg-gray-200 self-stretch shrink-0" />

              {store.ratings?.detailed_ratings && (
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-gray-900 mb-0.5">Detailed ratings</h3>
                  <p className="text-xs text-gray-500 mb-3">Average for the last 12 months</p>
                  <div className="space-y-2.5">
                    {(store.ratings.detailed_ratings?.accurate_description ?? 0) > 0 && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-700">Accurate description</span>
                        <DisplayStarRating rating={Number(store.ratings.detailed_ratings?.accurate_description ?? 0)} size="sm" />
                      </div>
                    )}
                    {(store.ratings.detailed_ratings?.shipping_speed ?? 0) > 0 && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-700">Shipping speed</span>
                        <DisplayStarRating rating={Number(store.ratings.detailed_ratings?.shipping_speed ?? 0)} size="sm" />
                      </div>
                    )}
                    {(store.ratings.detailed_ratings?.communication ?? 0) > 0 && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-700">Communication</span>
                        <DisplayStarRating rating={Number(store.ratings.detailed_ratings?.communication ?? 0)} size="sm" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="pt-6">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-bold text-gray-900">Customer reviews</h3>
                {store.ratings?.reviews && store.ratings.reviews.length > 5 && (
                  <button
                    type="button"
                    onClick={() => setShowAllReviews((v) => !v)}
                    className="text-sm font-medium text-green-600 hover:text-green-700"
                  >
                    {showAllReviews ? 'Show less' : `View all reviews (${store.ratings.reviews.length})`}
                  </button>
                )}
              </div>
              <div className="space-y-4">
                {store.ratings && store.ratings.reviews && store.ratings.reviews.length > 0 ? (
                  (showAllReviews ? store.ratings.reviews : store.ratings.reviews.slice(0, 5)).map((review: any) => (
                    <div key={review.id} className="border-b border-gray-200 pb-4 last:border-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                            {review.buyer_name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{review.buyer_name}</p>
                            <p className="text-xs text-gray-500">{formatDate(review.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <DisplayStarRating rating={review.rating} />
                        </div>
                      </div>
                      {(review.comment || review.message) && (
                        <p className="text-gray-700 mt-2 ml-12">{review.comment || review.message}</p>
                      )}
                      {(review.accurate_description || review.shipping_speed || review.communication) && (
                        <div className="mt-3 pt-3 border-t border-gray-100 ml-12">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            {review.accurate_description && (
                              <div>
                                <span className="text-gray-600">Accurate description: </span>
                                <span className="font-medium text-gray-900">
                                  {Number(review.accurate_description).toFixed(1)}
                                </span>
                                <div className="flex items-center mt-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                      key={star}
                                      className={`text-xs ${
                                        star <= Number(review.accurate_description)
                                          ? 'text-yellow-400'
                                          : 'text-gray-300'
                                      }`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {review.shipping_speed && (
                              <div>
                                <span className="text-gray-600">Shipping speed: </span>
                                <span className="font-medium text-gray-900">
                                  {Number(review.shipping_speed).toFixed(1)}
                                </span>
                                <div className="flex items-center mt-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                      key={star}
                                      className={`text-xs ${
                                        star <= Number(review.shipping_speed)
                                          ? 'text-yellow-400'
                                          : 'text-gray-300'
                                      }`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {review.communication && (
                              <div>
                                <span className="text-gray-600">Communication: </span>
                                <span className="font-medium text-gray-900">
                                  {Number(review.communication).toFixed(1)}
                                </span>
                                <div className="flex items-center mt-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                      key={star}
                                      className={`text-xs ${
                                        star <= Number(review.communication)
                                          ? 'text-yellow-400'
                                          : 'text-gray-300'
                                      }`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No reviews yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Store Listings */}
        <div className="bg-white rounded-lg shadow-md border-t border-gray-200 px-5 sm:px-8 pt-5 sm:pt-8 pb-6 sm:pb-8 mb-0">
          <div className="flex flex-row items-center justify-between mb-4 sm:mb-6 gap-3">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              Store Items
              {store.listings && store.listings.length > 0 && (
                <span className="text-base sm:text-lg font-normal text-gray-500 ml-2">
                  ({store.listings.length})
                </span>
              )}
            </h2>
            <div className="flex items-center gap-3 shrink-0">
              {store.listings && store.listings.length > storeItemsInitialCount && (
                <button
                  type="button"
                  onClick={() => {
                    if (storeItemsDisplayedCount > storeItemsInitialCount) {
                      setStoreItemsDisplayedCount(storeItemsInitialCount);
                    } else {
                      setStoreItemsDisplayedCount(
                        Math.min(
                          storeItemsInitialCount + storeItemsPageSize,
                          store.listings?.length ?? 0
                        )
                      );
                    }
                  }}
                  className="text-sm font-medium text-green-600 hover:text-green-700"
                >
                  {storeItemsDisplayedCount > storeItemsInitialCount ? 'Show less' : 'View all'}
                </button>
              )}
              {isOwner && (
                <Link
                  to="/sell"
                  className="bg-green-500 hover:bg-green-600 text-white font-medium px-4 py-2 rounded-lg transition text-sm whitespace-nowrap"
                >
                  Add in app
                </Link>
              )}
            </div>
          </div>

          {store.listings && store.listings.length > 0 ? (
            <>
              {user && selectedItems.size > 0 && (
                <div className="mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <span className="text-green-800 font-medium text-sm sm:text-base">
                    {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={() => {
                      if (!store.listings) return;
                      const listingsToCheckout: Listing[] = [];
                      store.listings.forEach((l: Listing) => {
                        if (selectedItems.has(l.id) && 
                            (l.quantity === undefined || l.quantity > 0) && 
                            !(l.sold === 1 || l.sold === true)) {
                          const qty = itemQuantities.get(l.id) || 1;
                          // Add the listing multiple times based on quantity
                          for (let i = 0; i < qty; i++) {
                            listingsToCheckout.push({ ...l });
                          }
                        }
                      });
                      if (listingsToCheckout.length > 0) {
                        navigate('/checkout', { state: { listings: listingsToCheckout } });
                      }
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white font-medium px-4 sm:px-6 py-2 rounded-lg transition text-sm sm:text-base w-full sm:w-auto"
                  >
                    Checkout Selected ({selectedItems.size})
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                {store.listings.slice(0, storeItemsDisplayedCount).map((listing: Listing) => {
                  const isSelected = selectedItems.has(listing.id);
                  const isSold = listing.sold === 1 || listing.sold === true;
                  const isOutOfStock = listing.quantity !== undefined && listing.quantity <= 0;
                  const isOwner = user && user.id === listing.user_id;
                  // Show button if item is available (not sold, not out of stock)
                  const canSelect = !isSold && !isOutOfStock;
                  const currentQuantity = itemQuantities.get(listing.id) || 1;
                  const hasMultipleQuantity = listing.quantity !== undefined && listing.quantity > 1;
                  
                  const handleAddToCart = () => {
                    if (!user) {
                      navigate('/login');
                      return;
                    }
                    if (isOwner) {
                      return;
                    }
                    const quantity = itemQuantities.get(listing.id) || 1;
                    
                    // Show "Adding" animation BEFORE adding to cart
                    setIsAddingToCart(true);
                    
                    addToCart(listing, quantity);
                    
                    // Hide animation after delay
                    setTimeout(() => {
                      setIsAddingToCart(false);
                    }, 1500);
                    
                    const newSelected = new Set(selectedItems);
                    if (isSelected) {
                      newSelected.delete(listing.id);
                      const newQuantities = new Map(itemQuantities);
                      newQuantities.delete(listing.id);
                      setItemQuantities(newQuantities);
                    } else {
                      newSelected.add(listing.id);
                      setItemQuantities(new Map(itemQuantities).set(listing.id, 1));
                    }
                    setSelectedItems(newSelected);
                  };
                  
                  return (
                    <div key={listing.id} className="relative">
                      {isSelected && hasMultipleQuantity && (
                        <div className="absolute top-2 left-2 z-30 bg-white rounded-lg border border-gray-200 shadow-lg p-2 flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const newQty = Math.max(1, currentQuantity - 1);
                              setItemQuantities(new Map(itemQuantities).set(listing.id, newQty));
                            }}
                            className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                          >
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <span className="text-sm font-semibold w-8 text-center">{currentQuantity}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const maxQty = listing.quantity || 1;
                              const newQty = Math.min(maxQty, currentQuantity + 1);
                              setItemQuantities(new Map(itemQuantities).set(listing.id, newQty));
                            }}
                            className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                          >
                            <i className="ri-add-line text-xs"></i>
                          </button>
                        </div>
                      )}
                      <ListingCard
                        showOpenStatus={false} 
                        listing={listing}
                        addButton={canSelect ? (
                          <button
                            type="button"
                            data-add-to-cart="true"
                            className={`add-to-cart-button w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${
                              isSelected 
                                ? 'bg-green-500 hover:bg-green-600 text-white scale-110' 
                                : 'bg-white text-black hover:bg-gray-50 hover:scale-110'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              e.nativeEvent?.stopImmediatePropagation();
                              handleAddToCart();
                            }}
                            style={{
                              boxShadow: isSelected 
                                ? '0 8px 16px rgba(34, 197, 94, 0.4)' 
                                : '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)'
                            }}
                            title={isSelected ? 'Remove from cart' : 'Add to cart'}
                          >
                            {isSelected ? (
                              <i className="ri-check-line text-lg font-bold"></i>
                            ) : (
                              <i className="ri-add-line text-base font-extrabold"></i>
                            )}
                          </button>
                        ) : undefined}
                      />
                    </div>
                  );
                })}
              </div>
              {storeItemsDisplayedCount < store.listings.length && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setStoreItemsDisplayedCount((prev) => Math.min(prev + storeItemsPageSize, store.listings?.length ?? 0))}
                    className="text-sm font-medium text-green-600 hover:text-green-700 py-2"
                  >
                    Load more
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg mb-4">No items available from this store yet.</p>
              {isOwner && (
                <Link
                  to="/sell"
                  className="inline-block bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-2 rounded-lg transition"
                >
                  Add items in the app
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Store Hours Modal */}
        {getStoreHoursModal()}
        
        {/* Store Details Modal */}
        {getStoreDetailsModal()}
      </div>
      
      {/* Adding to Cart Animation Bar */}
      {isAddingToCart && (
        <div className="fixed bottom-0 left-0 right-0 bg-red-500 text-white px-4 py-3 flex items-center gap-3 z-[9999] animate-slide-up shadow-lg">
          <i className="ri-shopping-cart-line text-xl"></i>
          <span className="font-medium">Adding</span>
        </div>
      )}
    </div>
  );
};

export default StorePage;

